import { brandPrivateFragment } from "@saleor/fragments/brands";
import { brandErrorFragment } from "@saleor/fragments/errors";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import {
  BrandCreateDeeplink,
  BrandCreateDeeplinkVariables
} from "./types/BrandCreateDeeplink";
import {
  BrandCreateSeller,
  BrandCreateSellerVariables
} from "./types/BrandCreateSeller";
import { brandUpdate, brandUpdateVariables } from "./types/brandUpdate";
import {
  shopifyBrandSync as ShopifyBrandSync,
  shopifyBrandSyncVariables as ShopifyBrandSyncVariables
} from "./types/shopifyBrandSync";
import {
  shopifyBrandTagAdd as ShopifyBrandTagAdd,
  shopifyBrandTagAddVariables as ShopifyBrandTagAddVariables
} from "./types/shopifyBrandTagAdd";
import {
  shopifyBrandTagRemove as ShopifyBrandTagRemove,
  shopifyBrandTagRemoveVariables as ShopifyBrandTagRemoveVariables
} from "./types/shopifyBrandTagRemove";
import {
  shopifyBrandTagUpdate as ShopifyBrandTagUpdate,
  shopifyBrandTagUpdateVariables as ShopifyBrandTagUpdateVariables
} from "./types/shopifyBrandTagUpdate";
import {
  shopifyCredentialsCheck as ShopifyCredentialsCheck,
  shopifyCredentialsCheckVariables as ShopifyCredentialsCheckVariables
} from "./types/shopifyCredentialsCheck";
import {
  UpdateBrandStatus,
  UpdateBrandStatusVariables
} from "./types/UpdateBrandStatus";

export const sellerUpdate = gql`
  ${brandPrivateFragment}
  ${brandErrorFragment}
  mutation brandUpdate($id: ID!, $input: BrandUpdateInput!) {
    brandUpdate(id: $id, input: $input) {
      errors: brandErrors {
        ...BrandErrorFragment
      }
      brand {
        ...BrandPrivateFragment
      }
    }
  }
`;

export const useSellerUpdate = makeMutation<brandUpdate, brandUpdateVariables>(
  sellerUpdate
);

export const sellerUpdateStatus = gql`
  ${brandPrivateFragment}
  ${brandErrorFragment}
  mutation UpdateBrandStatus($id: ID!, $status: SmopStatusEnum!) {
    updateBrandStatus(id: $id, status: $status) {
      errors: brandErrors {
        ...BrandErrorFragment
      }
      brand {
        ...BrandPrivateFragment
      }
    }
  }
`;

export const useSellerUpdateStatus = makeMutation<
  UpdateBrandStatus,
  UpdateBrandStatusVariables
>(sellerUpdateStatus);

export const sellerCreate = gql`
  ${brandPrivateFragment}
  ${brandErrorFragment}
  mutation BrandCreateSeller($input: BrandCreateInput!) {
    brandCreate(input: $input) {
      errors: brandErrors {
        ...BrandErrorFragment
      }
      brand {
        ...BrandPrivateFragment
      }
    }
  }
`;

export const useSellerCreate = makeMutation<
  BrandCreateSeller,
  BrandCreateSellerVariables
>(sellerCreate);

const brandCreateDeeplink = gql`
  mutation BrandCreateDeeplink($id: ID!) {
    brandGenerateDynamicLink(id: $id) {
      brandErrors {
        field
        code
        message
      }
      brand {
        id
        deeplinkUrl
        shortDynamicLink
      }
    }
  }
`;
export const useBrandCreateDeeplink = makeMutation<
  BrandCreateDeeplink,
  BrandCreateDeeplinkVariables
>(brandCreateDeeplink);

export const shopifyBrandSync = gql`
  mutation shopifyBrandSync($brand: ID!) {
    shopifyBrandSync(brand: $brand) {
      pid
      shopifyError {
        field
        message
        code
      }
    }
  }
`;

export const useShopifyBrandSync = makeMutation<
  ShopifyBrandSync,
  ShopifyBrandSyncVariables
>(shopifyBrandSync);

export const shopifyCredentialsCheck = gql`
  mutation shopifyCredentialsCheck($input: ShopifyCredentialsCheckInput!) {
    shopifyCredentialsCheck(input: $input) {
      shopifyError {
        field
        message
        code
      }
      status
    }
  }
`;

export const useShopifyCredentialsCheck = makeMutation<
  ShopifyCredentialsCheck,
  ShopifyCredentialsCheckVariables
>(shopifyCredentialsCheck);

export const shopifyTagsAdd = gql`
  mutation shopifyBrandTagAdd(
    $brandId: ID!
    $kind: ShopifyTagEnum!
    $tag: String!
  ) {
    shopifyBrandTagAdd(brandId: $brandId, kind: $kind, tag: $tag) {
      brand {
        id
        shopifyExclusionTags {
          tag
        }
        shopifyNonCodTags {
          tag
        }
      }
      shopifyError {
        field
        message
        code
      }
    }
  }
`;

export const useShopifyTagsAdd = makeMutation<
  ShopifyBrandTagAdd,
  ShopifyBrandTagAddVariables
>(shopifyTagsAdd);

export const shopifyTagsRemove = gql`
  mutation shopifyBrandTagRemove(
    $brandId: ID!
    $kind: ShopifyTagEnum!
    $tag: String!
  ) {
    shopifyBrandTagRemove(brandId: $brandId, kind: $kind, tag: $tag) {
      brand {
        id
        shopifyExclusionTags {
          tag
        }
        shopifyNonCodTags {
          tag
        }
      }
      shopifyError {
        field
        message
        code
      }
    }
  }
`;

export const useshopifyTagsRemove = makeMutation<
  ShopifyBrandTagRemove,
  ShopifyBrandTagRemoveVariables
>(shopifyTagsRemove);

export const shopifyTagsUpdate = gql`
  mutation shopifyBrandTagUpdate(
    $brandId: ID!
    $kind: ShopifyTagEnum!
    $newTag: String!
    $targetTag: String!
  ) {
    shopifyBrandTagUpdate(
      brandId: $brandId
      kind: $kind
      newTag: $newTag
      targetTag: $targetTag
    ) {
      brand {
        shopifyExclusionTags {
          tag
        }
        shopifyNonCodTags {
          tag
        }
      }
      shopifyError {
        field
        message
        code
      }
    }
  }
`;

export const useShopifyTagsUpdate = makeMutation<
  ShopifyBrandTagUpdate,
  ShopifyBrandTagUpdateVariables
>(shopifyTagsUpdate);
